import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { groupByProximity } from '../../lib/geo';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const STYLES = {
  dark: 'mapbox://styles/mapbox/navigation-night-v1',
  light: 'mapbox://styles/yoneldev/cmn6374fs005e01s05j2mdayf',
};

const US_CENTER = [-96, 38];

function getTheme() {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

// Co-located facilities are grouped into one marker — see lib/geo.js.
const groupPins = (pins) => groupByProximity(pins, p => [p.lat, p.lng]);

// Geographic view for the admin dashboard: one dot per geocoded LOCATION
// (a multi-location org pins every office), falling back to the org's own
// coordinates when it has no geocoded locations. Colored by type, popup
// with quick facts + a jump to the org profile. No coordinates = no pin
// (never a state-centroid fallback — see the 2026-06-07 misplacement
// lesson in DirectoryMap).
export default function AdminOrgMap({ orgs, locations = [], typeColor, focus = null }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLES[getTheme()],
      center: US_CENTER,
      zoom: 3.4,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left');
    map.addControl(new mapboxgl.FullscreenControl(), 'top-left');
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Follow the app theme
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (mapRef.current) mapRef.current.setStyle(STYLES[getTheme()]);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }

    // One pin per geocoded location; org-level pin only as fallback
    const locsByOrg = new Map();
    for (const l of locations) {
      if (l.lat == null || l.lng == null) continue;
      if (!locsByOrg.has(l.org_id)) locsByOrg.set(l.org_id, []);
      locsByOrg.get(l.org_id).push(l);
    }
    // An org's LOCATIONS are its pins — one per site. The org's own
    // coordinates are only a fallback when it has no geocoded locations:
    // every org's HQ is also a location row, so pinning both drew a bogus
    // "2" group marker on single-site practices.
    const pins = [];
    for (const org of orgs) {
      const locPins = (locsByOrg.get(org.id) || [])
        .map((l) => ({ org, lat: l.lat, lng: l.lng, locName: l.name, locCity: l.city, locState: l.state, isOrg: false }));
      if (locPins.length) {
        pins.push(...locPins);
      } else if (org.lat != null && org.lng != null) {
        pins.push({ org, lat: org.lat, lng: org.lng, locName: null, locCity: org.city, locState: org.state, isOrg: true });
      }
    }
    const bounds = new mapboxgl.LngLatBounds();
    const groups = groupPins(pins);

    for (let gi = 0; gi < groups.length; gi++) {
      const group = groups[gi];
      const primary = group.items.find(p => p.isOrg) || group.items[0];
      const org = primary.org;
      const coords = [Number(group.lng), Number(group.lat)];
      const color = typeColor(org.type);
      const stacked = group.items.length > 1;

      // Every single facility is the same dot, organization address or
      // location alike — kept in step with DirectoryMap so the two maps read
      // identically. Co-located facilities render as one badge showing how many.
      const el = document.createElement('div');
      if (stacked) {
        el.style.cssText = `
          min-width:20px;height:20px;padding:0 5px;border-radius:10px;
          background:${color};border:2.5px solid white;
          box-shadow:0 0 0 3px ${color}40, 0 2px 8px rgba(0,0,0,0.35);
          display:flex;align-items:center;justify-content:center;
          font:700 11px/1 Inter,-apple-system,sans-serif;color:#fff;cursor:pointer;`;
        el.textContent = String(group.items.length);
      } else {
        el.style.cssText = `
          width:16px;height:16px;border-radius:50%;
          background:${color};border:3px solid white;
          box-shadow:0 0 0 3px ${color}45, 0 2px 8px rgba(0,0,0,0.35);
          cursor:pointer;`;
      }

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (popupRef.current) popupRef.current.remove();

        const isDark = getTheme() === 'dark';
        const t = isDark
          ? { bg: '#14171f', text: '#f0f2f5', muted: '#a0a8be', border: 'rgba(255,255,255,0.08)', link: '#30C0DD' }
          : { bg: '#ffffff', text: '#111827', muted: '#6B7280', border: 'rgba(0,0,0,0.08)', link: '#0DA7CA' };
        const label = org.type === 'medical_practice' ? 'Medical' : 'Law firm';
        const sub = org.subscription?.status
          || ((org.type === 'law_firm' || org.type === 'marketer') ? 'free' : 'no sub');
        const place = [primary.locCity, primary.locState].filter(Boolean).join(', ');

        const entries = group.items.map((p, idx) => `
          <div style="padding:7px 0;${idx ? `border-top:1px solid ${t.border};` : ''}">
            <div style="font-size:12.5px;font-weight:700;color:${t.text};">${p.locName || p.org.name}</div>
            <div style="font-size:10.5px;color:${t.muted};margin-top:1px;">
              ${p.isOrg ? label : 'Location'}${p.locName ? ` · part of ${p.org.name}` : ''}
            </div>
            <button id="admin-map-open-${gi}-${idx}" style="margin-top:5px;width:100%;font-size:10.5px;font-weight:600;padding:5px 0;border-radius:7px;color:white;background:linear-gradient(135deg,#0DA7CA,#223368);border:none;cursor:pointer;">Open organization</button>
          </div>`).join('');

        const popup = new mapboxgl.Popup({ closeButton: false, offset: 14, maxWidth: 'none', className: 'admin-map-popup' })
          .setLngLat(coords)
          .setHTML(`
            <div style="font-family:Inter,-apple-system,sans-serif;width:230px;background:${t.bg};border-radius:12px;overflow:hidden;border:1px solid ${t.border};box-shadow:0 16px 48px rgba(0,0,0,${isDark ? '0.6' : '0.15'});">
              <div style="height:3px;background:${color};"></div>
              <div style="padding:12px 14px;max-height:340px;overflow-y:auto;">
                ${stacked ? `
                  <div style="font-size:11px;font-weight:700;color:${t.muted};text-transform:uppercase;letter-spacing:0.04em;margin-bottom:2px;">
                    ${group.items.length} facilities here
                  </div>
                  ${place ? `<div style="font-size:11px;color:${t.muted};margin-bottom:4px;">${place}</div>` : ''}
                  ${entries}
                ` : `
                  <div style="font-size:13px;font-weight:700;color:${t.text};margin-bottom:2px;">${primary.locName || org.name}</div>
                  ${primary.locName ? `<div style="font-size:11px;color:${t.muted};margin-bottom:2px;">part of ${org.name}</div>` : ''}
                  <div style="font-size:11px;color:${t.muted};margin-bottom:8px;">
                    <span style="color:${color};font-weight:600;">${primary.isOrg ? label : 'Location'}</span>
                    ${place ? ' · ' + place : ''}
                  </div>
                  <div style="font-size:11px;color:${t.muted};margin-bottom:10px;">
                    ${org.member_count} member${org.member_count === 1 ? '' : 's'} · ${org.partnership_count} partnership${org.partnership_count === 1 ? '' : 's'} · ${sub}
                  </div>
                  <button id="admin-map-open-${gi}-0" style="width:100%;font-size:11px;font-weight:600;padding:7px 0;border-radius:8px;color:white;background:linear-gradient(135deg,#0DA7CA,#223368);border:none;cursor:pointer;">Open organization</button>
                `}
              </div>
            </div>
          `)
          .addTo(map);
        popupRef.current = popup;

        group.items.forEach((p, idx) => {
          const btn = document.getElementById(`admin-map-open-${gi}-${idx}`);
          if (btn) btn.addEventListener('click', () => navigateRef.current(`/admin/organizations/${p.org.id}`));
        });
      });

      markersRef.current.push(new mapboxgl.Marker({ element: el, anchor: 'center' }).setLngLat(coords).addTo(map));
      bounds.extend(coords);
    }

    if (pins.length) {
      map.fitBounds(bounds, { padding: 60, maxZoom: 9, duration: 700 });
    }
  }, [orgs, locations, typeColor]);

  // Fly to a search result
  useEffect(() => {
    if (!mapRef.current || !focus?.lat || !focus?.lng) return;
    mapRef.current.flyTo({ center: [Number(focus.lng), Number(focus.lat)], zoom: 14, duration: 900 });
  }, [focus]);

  // Frameless popup chrome
  useEffect(() => {
    const s = document.createElement('style');
    s.textContent = `
      .admin-map-popup .mapboxgl-popup-content{background:transparent!important;box-shadow:none!important;padding:0!important;border-radius:12px!important}
      .admin-map-popup .mapboxgl-popup-tip{display:none!important}
    `;
    document.head.appendChild(s);
    return () => s.remove();
  }, []);

  return <div ref={containerRef} style={{ height: '100%', width: '100%' }} />;
}
